﻿import { BellRing, Cake, AlertTriangle, ChevronRight, CheckCircle2, Calendar } from 'lucide-react';
import { clsx } from 'clsx';

interface OperationalAlertsProps {
  unconfirmedCount: number;
  birthdayCount: number;
  debtorCount: number;
  loading?: boolean;
  onUnconfirmedClick: () => void;
  onBirthdayClick: () => void;
  onDebtorClick: () => void;
}

export function OperationalAlerts({
  unconfirmedCount,
  birthdayCount,
  debtorCount,
  loading = false,
  onUnconfirmedClick,
  onBirthdayClick,
  onDebtorClick,
}: OperationalAlertsProps) {
  const totalAlerts = unconfirmedCount + birthdayCount + debtorCount;

  return (
    <div className="bg-surface border border-border rounded-xl p-4 sm:p-5 shadow-xs space-y-3.5 transition-all">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-brand-primary/10 text-brand-primary">
            <BellRing size={18} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-text-primary">Alertas Operativas del Día</h3>
            <p className="text-xs text-text-muted">Acciones prioritarias de agenda y clientes</p>
          </div>
        </div>

        <div>
          {totalAlerts > 0 ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-brand-warning/15 text-brand-warning border border-brand-warning/30">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-warning animate-pulse" />
              {totalAlerts} {totalAlerts === 1 ? 'alerta activa' : 'alertas activas'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-brand-success/15 text-brand-success border border-brand-success/30">
              <CheckCircle2 size={13} />
              Todo al día
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
        {/* Card 1: Citas por Confirmar */}
        <button
          type="button"
          onClick={onUnconfirmedClick}
          className={clsx(
            'flex items-start justify-between p-3.5 rounded-xl border text-left transition-all duration-200 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500/40',
            unconfirmedCount > 0
              ? 'bg-amber-500/5 hover:bg-amber-500/10 border-amber-500/30 hover:border-amber-500/50 hover:shadow-xs'
              : 'bg-surface-secondary/60 hover:bg-surface-secondary border-border/70 text-text-muted opacity-80'
          )}
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div
              className={clsx(
                'p-2 rounded-lg shrink-0 transition-colors',
                unconfirmedCount > 0
                  ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                  : 'bg-surface-tertiary text-text-muted'
              )}
            >
              <Calendar size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span
                  className={clsx(
                    'text-xs font-bold truncate',
                    unconfirmedCount > 0
                      ? 'text-amber-900 dark:text-amber-100'
                      : 'text-text-primary'
                  )}
                >
                  Citas por Confirmar
                </span>
                <span
                  className={clsx(
                    'text-xs font-bold px-1.5 py-0.2 rounded-md shrink-0',
                    unconfirmedCount > 0
                      ? 'bg-amber-500/20 text-amber-800 dark:text-amber-200'
                      : 'bg-surface-tertiary text-text-muted'
                  )}
                >
                  {loading ? '...' : unconfirmedCount}
                </span>
              </div>
              <p className="text-[11px] text-text-secondary mt-0.5 line-clamp-2">
                {unconfirmedCount === 0
                  ? 'No hay citas pendientes hoy'
                  : unconfirmedCount === 1
                  ? '1 cita pendiente de confirmar hoy'
                  : `${unconfirmedCount} citas pendientes de confirmar hoy`}
              </p>
              <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 mt-2 group-hover:underline">
                <span>Gestionar en Agenda</span>
                <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </div>
        </button>

        {/* Card 2: Cumpleaños del Mes */}
        <button
          type="button"
          onClick={onBirthdayClick}
          className={clsx(
            'flex items-start justify-between p-3.5 rounded-xl border text-left transition-all duration-200 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500/40',
            birthdayCount > 0
              ? 'bg-pink-500/5 hover:bg-pink-500/10 border-pink-500/30 hover:border-pink-500/50 hover:shadow-xs'
              : 'bg-surface-secondary/60 hover:bg-surface-secondary border-border/70 text-text-muted opacity-80'
          )}
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div
              className={clsx(
                'p-2 rounded-lg shrink-0 transition-colors',
                birthdayCount > 0
                  ? 'bg-pink-500/20 text-pink-700 dark:text-pink-300'
                  : 'bg-surface-tertiary text-text-muted'
              )}
            >
              <Cake size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span
                  className={clsx(
                    'text-xs font-bold truncate',
                    birthdayCount > 0
                      ? 'text-pink-900 dark:text-pink-100'
                      : 'text-text-primary'
                  )}
                >
                  Cumpleaños del Mes
                </span>
                <span
                  className={clsx(
                    'text-xs font-bold px-1.5 py-0.2 rounded-md shrink-0',
                    birthdayCount > 0
                      ? 'bg-pink-500/20 text-pink-800 dark:text-pink-200'
                      : 'bg-surface-tertiary text-text-muted'
                  )}
                >
                  {loading ? '...' : birthdayCount}
                </span>
              </div>
              <p className="text-[11px] text-text-secondary mt-0.5 line-clamp-2">
                {birthdayCount === 0
                  ? 'Sin cumpleaños registrados este mes'
                  : birthdayCount === 1
                  ? '1 cliente cumple años este mes'
                  : `${birthdayCount} clientes cumplen años este mes`}
              </p>
              <div className="flex items-center gap-1 text-[11px] font-semibold text-pink-700 dark:text-pink-300 mt-2 group-hover:underline">
                <span>Felicitar en CRM</span>
                <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </div>
        </button>

        {/* Card 3: Saldos Deudores */}
        <button
          type="button"
          onClick={onDebtorClick}
          className={clsx(
            'flex items-start justify-between p-3.5 rounded-xl border text-left transition-all duration-200 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500/40',
            debtorCount > 0
              ? 'bg-brand-danger/5 hover:bg-brand-danger/10 border-brand-danger/30 hover:border-brand-danger/50 hover:shadow-xs'
              : 'bg-surface-secondary/60 hover:bg-surface-secondary border-border/70 text-text-muted opacity-80'
          )}
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div
              className={clsx(
                'p-2 rounded-lg shrink-0 transition-colors',
                debtorCount > 0
                  ? 'bg-brand-danger/20 text-brand-danger'
                  : 'bg-surface-tertiary text-text-muted'
              )}
            >
              <AlertTriangle size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <span
                  className={clsx(
                    'text-xs font-bold truncate',
                    debtorCount > 0
                      ? 'text-red-900 dark:text-red-100'
                      : 'text-text-primary'
                  )}
                >
                  Saldos Deudores
                </span>
                <span
                  className={clsx(
                    'text-xs font-bold px-1.5 py-0.2 rounded-md shrink-0',
                    debtorCount > 0
                      ? 'bg-brand-danger/20 text-brand-danger'
                      : 'bg-surface-tertiary text-text-muted'
                  )}
                >
                  {loading ? '...' : debtorCount}
                </span>
              </div>
              <p className="text-[11px] text-text-secondary mt-0.5 line-clamp-2">
                {debtorCount === 0
                  ? 'No hay saldos deudores pendientes'
                  : debtorCount === 1
                  ? '1 cliente con saldo deudor'
                  : `${debtorCount} clientes con saldo deudor`}
              </p>
              <div className="flex items-center gap-1 text-[11px] font-semibold text-brand-danger mt-2 group-hover:underline">
                <span>Gestionar en CRM</span>
                <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
